import { differenceInMinutes, formatDistanceToNowStrict, formatISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getStoredTimeZone } from './timeZones';
// These options mean your date will appear in the following format:
// 12 Mar 2024
export const dateTimeZoneFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
};
// These options mean the time associated with a date will appear using hours and minutes
// in a 24-hour clock format, ie 04:36 or 21:12
export const timeTimeZoneFormatOptions = {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
};
export const getLocalStorageSelectedTimeZone = () => {
    const storedTimeZone = getStoredTimeZone();
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return storedTimeZone?.value || browserTimeZone;
};
export const getNow = () => {
    const zoneValue = getLocalStorageSelectedTimeZone();
    const zonedDate = formatInTimeZone(new Date(), zoneValue, `yyyy/LL/dd HH:mm:ss xxx`);
    return new Date(zonedDate);
};
export const getNowInSeconds = () => Math.floor(Date.now() / 1000);
export const getDifferenceInMinutesFromNow = (dateStr) => differenceInMinutes(new Date(dateStr), getNow());
export const formatShortDate = (date) => {
    const zoneValue = getLocalStorageSelectedTimeZone();
    return formatInTimeZone(date, zoneValue, 'dd MMM yyyy');
};
export const formatDateTime = (date, withSeconds) => {
    const zoneValue = getLocalStorageSelectedTimeZone();
    return formatInTimeZone(date, zoneValue, `dd MMM yyyy, HH:mm${withSeconds ? ':ss' : ''}`);
};
export const getDaysLeft = (date) => formatDistanceToNowStrict(date, { unit: 'day' });
export const getTimeZoneName = () => {
    const offset = new Date().getTimezoneOffset();
    const hours = Math.abs(offset / 60);
    if (hours > 0) {
        return `GMT ${Math.sign(offset) === -1 ? '+' : '-'}${hours}`;
    }
    return 'GMT';
};
export const getLocalDateAndTime = ({ date, options, timeZone }) => {
    try {
        return date.toLocaleString('en-GB', {
            timeZone,
            ...options,
        });
    }
    catch {
        // if the time zone isn't recognised, fall back to the user's current local time zone
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return date.toLocaleString('en-GB', {
            timeZone: tz,
            ...options,
        });
    }
};
export const getISOFormattedUTCDateWithTimeZoneOffset = ({ date, utcOffsetString = 'Z', }) => {
    const formattedISO = formatISO(date, { format: 'extended' });
    const containsOffset = formattedISO.split('T')[1].includes('+') || formattedISO.split('T')[1].includes('-');
    if (containsOffset) {
        const withoutOffset = formattedISO.substring(0, formattedISO.length - 6);
        return `${withoutOffset}${utcOffsetString}`;
    }
    return `${formattedISO.substring(0, formattedISO.length - 1)}${utcOffsetString}`;
};
/**
 * Adds the start of date time and the end of day time, and utc offset, to date range queries that
 * are in the format of "yyyyMMdd..yyyyMMdd".
 *
 * Output string is formatted as "yyyyMMddTHHmmsszzz..yyyyMMddTHHmmsszzz".
 */
export const appendTimeToDateRangeQuery = (dateRangeQuery, utcOffsetString = '+00:00') => {
    const [fromDate, toDate] = dateRangeQuery.split('..');
    const utcOffsetWithoutColon = utcOffsetString.replace(':', '');
    const fromDateWithTime = `${fromDate}T000000${utcOffsetWithoutColon}`;
    const toDateWithTime = `${toDate}T235959${utcOffsetWithoutColon}`;
    return `${fromDateWithTime}..${toDateWithTime}`;
};
